import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../common/Toast';
import CustomSelect from '../common/CustomSelect';
import UserSelect from '../common/UserSelect';
import MarkdownEditor from '../common/MarkdownEditor';
import InlinePillSelect from '../common/InlinePillSelect';
import { createWorkItem, updateWorkItem } from '../../api/workItems';
import {
  WORK_ITEM_TYPES, WORK_ITEM_TYPE_LABELS, WORK_ITEM_TYPE_COLORS,
  WORK_ITEM_PRIORITY_META, DEFAULT_WORK_ITEM_STATUS,
} from '../../constants/workItem';

const PillCaret = () => (
  <svg className="ips-caret" width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const PRIORITY_OPTIONS = [
  { value: 1, label: '低' }, { value: 2, label: '中' }, { value: 3, label: '高' }, { value: 4, label: '緊急' },
];

const WorkItemModal = ({
  state, projectId, projects, groups, companyUsers,
  companyId, departmentId, creatorId, onClose, onSaved,
}) => {
  const toast = useToast();
  const isEdit = state.mode === 'edit';
  const isSubItem = !!state.parentId;
  const src = state.item || {};

  // 現有指派:第一個當 Owner,其餘協作者
  const existingAssignees = (src.assignees || []).map((a) => a.user_id);

  const [form, setForm] = useState({
    project_id: isEdit ? (src.project_id || null) : (projectId || null),
    title: src.title || '',
    type: src.type || 'task',
    priority: src.priority ?? 2,
    start_date: src.start_date || '',
    end_date: src.end_date || '',
    description: src.description || '',
  });
  const [ownerId, setOwnerId] = useState(isEdit ? (existingAssignees[0] || null) : (creatorId || null));
  const [collaboratorIds, setCollaboratorIds] = useState(isEdit ? existingAssignees.slice(1) : []);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('請輸入標題'); return; }
    if (!isSubItem && !form.project_id) { toast.error('請選擇專案'); return; }
    if (!ownerId) { toast.error('請選擇 Owner'); return; }
    setSaving(true);

    const assignee_ids = [ownerId, ...collaboratorIds.filter((id) => id !== ownerId)];
    const payload = {
      title: form.title.trim(),
      type: form.type,
      priority: form.priority,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      description: form.description || null,
    };

    let res;
    if (isEdit) {
      res = await updateWorkItem(src.id, { ...payload, project_id: form.project_id });
      // 指派變更(edit)在 detail/inline 另做,這裡先只更新欄位
    } else {
      res = await createWorkItem({
        ...payload,
        project_id: isSubItem ? projectId : form.project_id,
        parent_id: state.parentId || null,
        group_id: state.groupId || null,
        status: DEFAULT_WORK_ITEM_STATUS,
        company_id: companyId,
        department_id: departmentId,
        creator_id: creatorId,
        assignee_ids,
      });
    }
    setSaving(false);
    if (res.error) { toast.error(isEdit ? '更新失敗' : '建立失敗'); return; }
    toast.success(isEdit ? '已更新' : '已建立工作項目');
    onSaved();
  };

  const projectOptions = (projects || []).map((p) => ({ value: p.id, label: p.name }));
  const title = isEdit ? '編輯工作項目' : (isSubItem ? '新增子項' : '新增工作項目');

  // Portal 到 body:脫離 BetaLayout 的 perspective/transform 3D 容器,否則 fixed 遮罩不會相對視窗全螢幕
  return createPortal(
    <div className="wi-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wi-modal wi-modal--wide">
        <div className="wi-modal-head">
          <h2>{title}</h2>
          <button className="wi-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="wi-modal-body wi-modal-body--split">
          {/* 左欄 */}
          <div className="wi-modal-left">
            {!isSubItem && (
              <div className="wi-field">
                <span className="wi-label">專案 *</span>
                <CustomSelect
                  value={form.project_id}
                  onChange={(v) => set('project_id', v)}
                  options={projectOptions}
                  placeholder="選擇專案…"
                  searchable
                  portal
                />
              </div>
            )}

            <label className="wi-field">
              <span className="wi-label">標題 *</span>
              <input autoFocus value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="輸入標題…" />
            </label>

            <div className="wi-field">
              <span className="wi-label">Owner *</span>
              <UserSelect
                values={ownerId ? [ownerId] : []}
                onChange={(ids) => {
                  const newOwner = ids[0] || null;
                  if (newOwner && collaboratorIds.includes(newOwner)) {
                    setCollaboratorIds((prev) => [...prev.filter((id) => id !== newOwner), ...(ownerId ? [ownerId] : [])]);
                  }
                  setOwnerId(newOwner);
                }}
                users={companyUsers}
                placeholder="選擇負責人…"
                singleSelect
              />
            </div>

            <div className="wi-field">
              <span className="wi-label">協作者</span>
              <UserSelect
                values={collaboratorIds}
                onChange={setCollaboratorIds}
                users={companyUsers}
                excludeIds={ownerId ? [ownerId] : []}
                placeholder="選擇協作者…"
              />
            </div>

            <div className="wi-field-row">
              <div className="wi-field">
                <span className="wi-label">類型</span>
                <InlinePillSelect
                  value={form.type}
                  options={WORK_ITEM_TYPES.map((t) => ({ value: t, label: WORK_ITEM_TYPE_LABELS[t], color: WORK_ITEM_TYPE_COLORS[t].fg }))}
                  onChange={(v) => set('type', v)}
                  trigger={<span className="ips-pill" style={{ background: WORK_ITEM_TYPE_COLORS[form.type].bg, color: WORK_ITEM_TYPE_COLORS[form.type].fg }}>◇ {WORK_ITEM_TYPE_LABELS[form.type]}<PillCaret /></span>}
                />
              </div>
              <div className="wi-field">
                <span className="wi-label">優先度</span>
                <InlinePillSelect
                  value={form.priority}
                  options={PRIORITY_OPTIONS.map((p) => ({ value: p.value, label: p.label, color: WORK_ITEM_PRIORITY_META[p.value].color }))}
                  onChange={(v) => set('priority', v)}
                  trigger={<span className="ips-pill" style={{ background: WORK_ITEM_PRIORITY_META[form.priority].color, color: '#fff' }}>{WORK_ITEM_PRIORITY_META[form.priority].label}<PillCaret /></span>}
                />
              </div>
            </div>

            <div className="wi-field">
              <span className="wi-label">排程</span>
              <div className="wi-field-row">
                <input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />
                <input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} />
              </div>
            </div>
          </div>

          {/* 右欄 */}
          <div className="wi-modal-right">
            <span className="wi-label">說明</span>
            <div className="wi-md-wrap">
              <MarkdownEditor value={form.description} onChange={(v) => set('description', v)} placeholder="輸入說明（支援 Markdown、可拖曳 / 貼上圖片）…" linkTickets={false} />
            </div>
          </div>
        </div>

        <div className="wi-modal-foot wi-modal-foot--split">
          <span className="wi-foot-hint">💡 建立後可在詳情拆解成子項 · function list</span>
          <div className="wi-foot-actions">
            <button className="wi-btn-secondary" onClick={onClose} disabled={saving}>取消</button>
            <button className="wi-btn-primary" onClick={handleSave} disabled={saving || !form.title.trim()}>
              {saving ? '儲存中…' : (isEdit ? '儲存' : '建立工作項目')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WorkItemModal;
